export async function handleHttpErrors(response: Response): Promise<void> {
    switch (response.status) {
        case 204:
            throw new Error(
                'No content: The request was successful but returned no content.'
            );

        case 401:
            throw new Error('Unauthorized: Please check your credentials.');

        case 403:
            throw new Error(
                'Forbidden: You do not have permission to access this resource.'
            );

        case 404:
            throw new Error(
                'Not Found: The requested resource could not be found.'
            );

        case 405:
            throw new Error(
                'Method Not Allowed: The HTTP method is not allowed for this resource.'
            );

        case 408:
            throw new Error(
                'Request Timeout: The server timed out waiting for the request.'
            );

        case 429:
            throw new Error(
                'Too Many Requests: You have sent too many requests in a given amount of time.'
            );

        case 500:
            throw new Error(
                'Internal Server Error: Something went wrong on the server.'
            );

        case 502:
            throw new Error(
                'Bad Gateway: The server received an invalid response from the upstream server.'
            );

        case 503:
            throw new Error(
                'Service Unavailable: The server is temporarily unavailable, possibly due to maintenance.'
            );

        case 504:
            throw new Error(
                'Gateway Timeout: The server did not receive a timely response from the upstream server.'
            );

        default:
            if (!response.ok) {
                // Handle any other non-2xx status codes
                throw new Error(`Unexpected Error: ${response.statusText}`);
            }
            // If the response is OK, just return without errors
            return;
    }
}

export const genericNetworkError = {
    success: false,
    message: 'A Network Error Has Occurred. Please Try Again',
    body: null,
};

export function buildApiResult(result: Response, body: any) {
    return {
        status: result?.status,
        ok: result?.ok,
        body,
    };
}

export type FetchWithTimeoutOptions = RequestInit & {
    timeout?: number;
};

export function fetchWithTimeout(
    input: RequestInfo | URL,
    init: FetchWithTimeoutOptions = {}
): Promise<Response> {
    const { timeout = 5000, ...fetchOptions } = init;

    return new Promise((resolve, reject) => {
        const controller = new AbortController();
        const { signal } = controller;

        const timeoutId = setTimeout(() => {
            controller.abort();
            reject(new Error('Request timed out'));
        }, timeout);

        // If a custom signal exists, listen for abort events on it as well
        if (fetchOptions.signal) {
            fetchOptions.signal.addEventListener('abort', () => {
                controller.abort();
            });
        }

        // Helper function to clean up resources
        const cleanup = () => {
            clearTimeout(timeoutId);
        };

        // Perform the fetch with the abort signal
        fetch(input, { ...fetchOptions, signal })
            .then((response) => {
                cleanup();
                resolve(response);
            })
            .catch((error) => {
                cleanup();
                if (error.name === 'AbortError') {
                    reject(new Error('Request timed out'));
                } else {
                    reject(error);
                }
            });
    });
}
